import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { MessageSquare, Video, Check, Loader2, Send, Play } from "lucide-react";
import { toast } from "sonner";

const TIER_ICON = { chat: MessageSquare, form_check: Video };

export default function Coaching() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(null);

  useSEO({
    title: "Ronin Coaching — 1:1 with Coach Chris | Better Through Boxing",
    description: "Personal boxing coaching from Coach Chris. Chat coaching for general advice ($10/mo) and detailed video form-checks ($40/mo). Cancel anytime.",
    path: "/coaching",
    keywords: ["online boxing coach", "boxing form check", "1 on 1 boxing coaching", "boxing coaching subscription"],
  });

  const loadMe = () => { if (user) api.get("/coaching/me").then((r) => setMe(r.data)).catch(() => {}); };
  useEffect(() => { api.get("/coaching/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {}); }, []);
  useEffect(() => { loadMe(); }, [user]);

  const subscribe = async (tier) => {
    if (!user) { navigate("/login?next=/coaching"); return; }
    setBusy(tier);
    try {
      const { data } = await api.post("/coaching/subscribe", { tier, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  const cancel = async (tier) => {
    try { await api.post("/coaching/cancel", { tier }); toast.success("Subscription will end at the period close."); loadMe(); }
    catch { toast.error("Could not cancel"); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <section className="max-w-5xl mx-auto px-6 pt-16 pb-10">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">1:1 with Coach Chris</p>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-3">Ronin Coaching</h1>
        <p className="text-zinc-400 text-base mt-4 max-w-2xl">Real feedback from a real coach — no bots, no generic plans. Get your questions answered and your technique dialed in. Cancel anytime.</p>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-16 grid md:grid-cols-2 gap-6">
        {plans.map((plan) => {
          const Icon = TIER_ICON[plan.tier] || MessageSquare;
          const state = me?.[plan.tier];
          const active = state?.active;
          return (
            <div key={plan.tier} data-testid={`coaching-plan-${plan.tier}`}
              className={`border ${active ? "border-[#FF331F]" : "border-[#27272A]"} bg-[#0F0F0F] p-7 flex flex-col`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#FF331F]/10 flex items-center justify-center">
                  <Icon className="h-5 w-5 text-[#FF331F]" />
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-xl uppercase tracking-tight">{plan.name}</h2>
                  <div className="text-2xl font-bold">${plan.price.toFixed(0)}<span className="text-sm text-zinc-500 font-normal"> / month</span></div>
                </div>
                {active && <span className="bg-[#FF331F] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm">Active</span>}
              </div>
              <p className="text-zinc-400 text-sm mt-4">{plan.description}</p>
              <ul className="mt-4 space-y-2 flex-1">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                    <Check className="h-4 w-4 text-[#FF331F] mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              {plan.tier === "form_check" && active && (
                <p className="text-xs text-zinc-500 italic mt-4">
                  {me?.form_checks_used_this_month >= 1 ? "Used this month — resets on the 1st" : "1 form-check available this month"}
                </p>
              )}
              {plan.tier === "chat" && active && (
                <p className="text-xs text-zinc-500 italic mt-4" data-testid="chat-usage">
                  {(me?.chat_questions_used_this_month ?? 0)}/{me?.chat_monthly_limit ?? 5} questions used this month
                </p>
              )}
              <div className="mt-6">
                {active ? (
                  state?.cancel_at_period_end
                    ? <p className="text-xs text-zinc-500 text-center">Cancels at period end</p>
                    : <button onClick={() => cancel(plan.tier)} data-testid={`coaching-cancel-${plan.tier}`} className="text-xs text-zinc-500 underline w-full text-center">Cancel subscription</button>
                ) : (
                  <Button onClick={() => subscribe(plan.tier)} disabled={!!busy} data-testid={`coaching-subscribe-${plan.tier}`}
                    className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-xs">
                    {busy === plan.tier ? <Loader2 className="h-4 w-4 animate-spin" /> : `Subscribe · $${plan.price.toFixed(0)}/mo`}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </section>

      {user && me && (me.chat?.active || me.form_check?.active) && (
        <section className="max-w-5xl mx-auto px-6 pb-20 grid lg:grid-cols-2 gap-6">
          {me.chat?.active && <ChatPanel />}
          {me.form_check?.active && <FormCheckPanel me={me} onSubmitted={loadMe} />}
        </section>
      )}

      <SiteFooter />
    </div>
  );
}

function ChatPanel() {
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = () => api.get("/coaching/threads").then((r) => setThread((r.data.threads || []).find((t) => t.type === "chat") || null)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async () => {
    const text = body.trim(); if (!text) return;
    setSending(true); setBody("");
    try { const { data } = await api.post("/coaching/chat", { body: text }); setThread(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); setBody(text); }
    finally { setSending(false); }
  };

  const messages = thread?.messages || [];
  return (
    <div className="border border-[#27272A] bg-[#0F0F0F] flex flex-col" style={{ height: 480 }} data-testid="coaching-chat-panel">
      <div className="border-b border-[#27272A] px-5 py-4 flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-[#FF331F]" />
        <span className="font-display uppercase tracking-widest text-sm">Chat with Coach</span>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-10">Ask Coach Chris your first question.</p>
        ) : messages.map((m) => (
          <div key={m.id} className={`max-w-[85%] p-3 rounded-sm ${m.sender === "coach" ? "bg-[#FF331F]/10 border border-[#FF331F]" : "bg-[#161616] border border-[#27272A] ml-auto"}`}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">{m.sender === "coach" ? "Coach Chris" : "You"}</div>
            <div className="text-sm text-zinc-100 whitespace-pre-wrap">{m.body}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="border-t border-[#27272A] p-3 flex gap-2">
        <input value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type your question…" data-testid="coaching-chat-input"
          className="flex-1 bg-black border border-[#27272A] text-white text-sm px-3 py-2 rounded-sm" />
        <Button onClick={send} disabled={sending || !body.trim()} data-testid="coaching-chat-send" className="bg-[#FF331F] hover:bg-[#FF5242]">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function FormCheckPanel({ me, onSubmitted }) {
  const [threads, setThreads] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const used = (me?.form_checks_used_this_month ?? 0) >= 1;

  const load = () => api.get("/coaching/threads").then((r) => setThreads((r.data.threads || []).filter((t) => t.type === "form_check"))).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!/^https?:\/\//i.test(videoUrl.trim())) { toast.error("Paste a valid video link (https://…)"); return; }
    setSubmitting(true);
    try {
      await api.post("/coaching/form-check", { video_url: videoUrl.trim(), note: note.trim() });
      setVideoUrl(""); setNote(""); load(); onSubmitted?.();
      toast.success("Sent! Coach Chris will review your video.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="border border-[#27272A] bg-[#0F0F0F]" data-testid="coaching-formcheck-panel">
      <div className="border-b border-[#27272A] px-5 py-4 flex items-center gap-2">
        <Video className="h-4 w-4 text-[#FF331F]" />
        <span className="font-display uppercase tracking-widest text-sm">Form Check</span>
      </div>
      <div className="p-5">
        {used ? (
          <p className="text-sm text-zinc-400">You’ve used this month’s form-check. Your next one unlocks on the 1st.</p>
        ) : (
          <>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Video link</label>
            <input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://youtu.be/…  (unlisted is fine)"
              data-testid="coaching-fc-url" className="mt-1 w-full bg-black border border-[#27272A] text-white text-sm px-3 py-2 rounded-sm" />
            <p className="text-[11px] text-zinc-500 mt-1">Upload a ≤3 min clip to YouTube as Unlisted (or any link Coach can open) and paste it here.</p>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold block mt-3">Focus (optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. my jab and footwork"
              data-testid="coaching-fc-note" className="mt-1 w-full bg-black border border-[#27272A] text-white text-sm px-3 py-2 rounded-sm" />
            <Button onClick={submit} disabled={submitting} data-testid="coaching-fc-submit"
              className="mt-3 w-full bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-xs">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send for review"}
            </Button>
          </>
        )}

        {threads.length > 0 && (
          <div className="mt-5 space-y-3">
            {threads.map((t) => {
              const reply = [...(t.messages || [])].reverse().find((m) => m.sender === "coach");
              return (
                <div key={t.id} className="border border-[#27272A] p-3" data-testid={`coaching-fc-thread-${t.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <a href={t.video_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm text-zinc-200 hover:text-white">
                      <Play className="h-4 w-4 text-[#FF331F]" /> View submitted video
                    </a>
                    <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm ${t.status === "answered" ? "bg-[#FF331F] text-white" : "bg-[#161616] text-zinc-400 border border-[#27272A]"}`}>
                      {t.status === "answered" ? "Reviewed" : "Pending"}
                    </span>
                  </div>
                  {reply ? (
                    <div className="mt-2 p-3 bg-[#FF331F]/8 border-l-2 border-[#FF331F]">
                      <div className="text-[10px] uppercase tracking-widest text-[#FF331F] font-bold mb-1">Coach Chris</div>
                      <div className="text-sm text-zinc-100 whitespace-pre-wrap">{reply.body}</div>
                    </div>
                  ) : <p className="text-xs text-zinc-500 italic mt-2">Coach Chris is reviewing your video.</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
